import { useEffect, useState, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../store';
import { 
  fetchContent, 
  selectRegularVideos, 
  selectRegularLoading, 
  selectRegularError,
  selectShorts,
  selectShortsLoading,
  selectShortsError,
  clearContentList
} from '../store/content/slice';
import { selectAllTopics } from '../store/topics/slice';
import { ContentService } from '../services/content';
import { CONTENT_SORT_VALUES, CONTENT_TYPES, DURATION_RANGES } from '../constants/content';
import { VideoContent, ContentSearchParams } from '../types/content';
import { TopicWithVirtuals as Topic } from '../types/topic';

export interface TopicSection {
  id: string;
  title: string;
  videos: VideoContent[];
  loading: boolean;
  error: string | null;
}

export const useHomeContent = () => {
  const dispatch = useAppDispatch();
  const regularVideos = useAppSelector(state => selectRegularVideos(state) || []);
  const isRegularLoading = useAppSelector(state => selectRegularLoading(state) || false);
  const regularError = useAppSelector(state => selectRegularError(state) || null);
  const shorts = useAppSelector(state => selectShorts(state) || []);
  const isShortsLoading = useAppSelector(state => selectShortsLoading(state) || false);
  const shortsError = useAppSelector(state => selectShortsError(state) || null);
  const topics = useAppSelector(state => selectAllTopics(state) || []);

  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const [topicSections, setTopicSections] = useState<TopicSection[]>([]);
  const [isLoadingTopicSections, setIsLoadingTopicSections] = useState(false);

  // Select the topic with most videos by default only on desktop
  useEffect(() => {
    const isMobile = window.innerWidth < 640; // sm breakpoint in Tailwind
    if (!isMobile && topics.length > 0 && selectedTopics.length === 0) {
      const topicWithMostVideos = [...topics].sort((a, b) => (b.count || 0) - (a.count || 0))[0];
      if (topicWithMostVideos) {
        setSelectedTopics([topicWithMostVideos.id]);
      }
    }
  }, [topics, selectedTopics.length]);

  // Memoize the topic map for efficient lookups
  const topicMap = useMemo(() => {
    return topics.reduce((acc, topic) => {
      if (topic.id && topic.title) {
        acc[topic.id] = {
          ...topic,
          status: 'public',
          id: topic.id,
          title: topic.title,
          description: topic.description || '',
          count: topic.count || 0,
          organization: topic.organization || '',
          parent: topic.parent || null,
          children: Array.isArray(topic.children) ? topic.children : [],
          youtube: topic.youtube || '',
          thumbnail: topic.thumbnail || '',
          createdAt: topic.createdAt || new Date(),
          updatedAt: topic.updatedAt || new Date(),
          getFullPath: () => topic.title,
          hasChildren: () => Array.isArray(topic.children) && topic.children.length > 0
        };
      }
      return acc;
    }, {} as Record<string, Topic>);
  }, [topics]);

  // Fetch initial content
  useEffect(() => {
    // Fetch trending shorts
    dispatch(
      fetchContent({
        type: CONTENT_TYPES.VIDEO,
        contentType: 'shorts',
        duration: DURATION_RANGES.SHORTS,
        sortBy: CONTENT_SORT_VALUES.CREATED_AT,
        sortType: 'desc',
        limit: 10,
        page: 1
      })
    );

    // Fetch latest regular videos
    dispatch(
      fetchContent({
        type: CONTENT_TYPES.VIDEO,
        contentType: 'regular',
        sortBy: CONTENT_SORT_VALUES.CREATED_AT,
        sortType: 'desc',
        limit: 10,
        page: 1
      })
    );

    return () => {
      dispatch(clearContentList('regular'));
      dispatch(clearContentList('shorts'));
    };
  }, [dispatch]);

  // Fetch topic sections
  useEffect(() => {
    const fetchTopicSections = async () => {
      if (!selectedTopics.length) {
        setTopicSections([]);
        return;
      }

      setIsLoadingTopicSections(true);

      try {
        const contentService = ContentService.getInstance();
        
        // Single API call for all topics
        const searchParams: ContentSearchParams = {
          type: CONTENT_TYPES.VIDEO,
          topics: selectedTopics.join(','),
          sortBy: CONTENT_SORT_VALUES.CREATED_AT,
          sortType: 'desc',
          limit: 10,
          page: 1
        };

        const response = await contentService.searchContent(searchParams);

        // Create sections for each selected topic
        const sections = selectedTopics.map(topicId => {
          const topic = topicMap[topicId];
          const topicVideos = response.results.filter((item): item is VideoContent => {
            return item.type === CONTENT_TYPES.VIDEO && item.topics.includes(topicId);
          });

          return {
            id: topicId,
            title: topic?.title || 'Unknown Topic',
            videos: topicVideos,
            loading: false,
            error: null,
          };
        });

        setTopicSections(sections);
      } catch (error) {
        console.error('Failed to fetch topic sections:', error);
        setTopicSections(selectedTopics.map(id => ({
          id,
          title: topicMap[id]?.title || 'Unknown Topic',
          videos: [],
          loading: false,
          error: 'Failed to load videos for this topic',
        })));
      } finally {
        setIsLoadingTopicSections(false);
      }
    };

    fetchTopicSections();
  }, [selectedTopics, topicMap]);

  return {
    shorts,
    isShortsLoading,
    shortsError,
    regularVideos,
    isRegularLoading,
    regularError,
    topicSections,
    isLoadingTopicSections,
    selectedTopics,
    setSelectedTopics,
  };
}; 